/**
 * Newsletter Modal Component
 * Main modal wrapper with step navigation and animations
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { dataLayerPushClickButton } from '@/lib/ga4';
import { useNewsletter } from '../hooks/useNewsletter';
import { MODAL_CONTENT } from '../constants';
import { NewsletterStep1 } from './NewsletterStep1';
import { NewsletterStep2 } from './NewsletterStep2';
import { NewsletterStep3 } from './NewsletterStep3';
import { NewsletterAlreadySubscribed } from './NewsletterAlreadySubscribed';

export function NewsletterModal() {
  const { isModalOpen, closeModal, currentStep, alreadySubscribed } = useNewsletter();
  const [animatingStep, setAnimatingStep] = useState(currentStep);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle step transitions with animation
  useEffect(() => {
    if (currentStep !== animatingStep) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setAnimatingStep(currentStep);
        setIsAnimating(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [currentStep, animatingStep]);

  const renderStep = () => {
    if (alreadySubscribed) {
      return <NewsletterAlreadySubscribed />;
    }

    switch (animatingStep) {
      case 1:
        return <NewsletterStep1 />;
      case 2:
        return <NewsletterStep2 />;
      case 3:
        return <NewsletterStep3 />;
      default:
        return <NewsletterStep1 />;
    }
  };

  return (
    <DialogPrimitive.Root open={isModalOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogPrimitive.Portal>
        {/* Overlay */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[9999] bg-[#00000040] backdrop-blur-[3px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />

        {/* Content */}
        <DialogPrimitive.Content
          className={cn(
            'fixed z-[10000] bg-white shadow-2xl',
            // Positioning
            'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            // Size - responsive
            'w-[calc(100%-2rem)] max-w-md',
            // Height constraints
            'max-h-[90vh] tablet:max-h-[85vh]',
            // Shape
            'rounded-2xl tablet:rounded-3xl',
            // Overflow
            'overflow-hidden',
            // Flex column so children can scroll within the max-height
            'flex flex-col',
            // Animation
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-300'
          )}
        >
          {/* Header */}
          <div className="relative shrink-0 bg-gradient-to-r from-[#014383] to-[#0159a8] px-5 py-4 tablet:px-6 tablet:py-5">
            {/* Progress dots - only show for steps 1 and 2 */}
            {!alreadySubscribed && currentStep < 3 && (
              <div className="flex items-center gap-2 mb-2">
                {[1, 2].map((step) => (
                  <div
                    key={step}
                    className={cn(
                      'h-1.5 rounded-full transition-all duration-300',
                      step === currentStep
                        ? 'w-6 bg-white'
                        : step < currentStep
                          ? 'w-1.5 bg-white/80'
                          : 'w-1.5 bg-white/40'
                    )}
                  />
                ))}
              </div>
            )}

            <DialogPrimitive.Title className="text-lg tablet:text-xl font-bold text-white pr-10">
              {MODAL_CONTENT.title}
            </DialogPrimitive.Title>

            {/* Close button */}
            <DialogPrimitive.Close
              onClick={() => dataLayerPushClickButton('Κλείσιμο', 'Close')}
              className={cn(
                'absolute right-4 top-4',
                'inline-flex items-center justify-center rounded-full p-2',
                'text-white/80 hover:text-white hover:bg-white/10',
                'transition-colors duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50'
              )}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Κλείσιμο</span>
            </DialogPrimitive.Close>
          </div>

          {/* Step content with animation */}
          <div
            className={cn(
              'flex-1 min-h-0 flex flex-col transition-all duration-150',
              isAnimating ? 'opacity-0 translate-x-2' : 'opacity-100 translate-x-0'
            )}
          >
            {renderStep()}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
